<script lang="ts" setup>
import { ArrowRightIcon, FileIcon } from 'lucide-vue-next'

const { t } = useI18n()

const {
  isAdmin,
  isProducer,
} = storeToRefs(useAuthStore())

const { data: userOnboardingData, isLoading: userOnboardingLoading } = useCurrentUserOnboarding(true)

// When onboarding is done, we don't need to show the first steps
const showFirstSteps = computed(() => featureFlags.onboarding.value && !userOnboardingLoading.value && !userOnboardingData.value?.onboardingDone)
const firstStepsLoading = computed(() => userOnboardingLoading.value && featureFlags.onboarding.value)
const onboardingCompletion = computed(() => (userOnboardingData.value?.onboarding.onboardingItems?.filter(item => item.done).length ?? 0) / (userOnboardingData.value?.onboarding.onboardingItems?.length ?? 0) * 100)

const firstStepCards = computed(() => {
  return {
    key: 'first_steps.first_steps_cinqo',
    done: false,
    value: '1',
    icon: FileIcon,
  }
})
</script>

<template>
  <div v-if="isProducer && !isAdmin" class="space-y-4">
    <TemporaryRoundInformationsPSOA />

    <div v-if="firstStepsLoading">
      <div class="skeleton h-48 w-full" />
    </div>

    <FirstStepsCard
      v-if="showFirstSteps && !userOnboardingData?.onboarding.done"
      :key="firstStepCards.key"
      :done="firstStepCards.done"
      :translation-key="firstStepCards.key"
      :value="(firstStepCards.value as string)"
    >
      <template #title>
        <div class="flex gap-2 items-center">
          {{ t(`${firstStepCards.key}.title`) }}

          <Badge color="warning">
            <ArrowRightIcon :size="12" />
            {{ t('first_steps.to_complete') }}
          </Badge>
        </div>
      </template>

      <!-- Document card has a its own right content -->
      <template #rightContent>
        <div
          class="absolute right-5 top-1/2 -translate-y-1/2 radial-progress text-xs border-8 bg-[#5b38db] border-[#5b38db]"
          :style="`--value:${onboardingCompletion}; --size:6rem; --thickness: 6px;`"
          role="progressbar"
        >
          <div class="flex flex-col items-center justify-center">
            <span class="font-semibold text-lg">
              {{ onboardingCompletion }}%
            </span>
            <span>{{ t('first_steps.completed') }}</span>
          </div>
        </div>
      </template>
    </FirstStepsCard>

    <UnratedCollectsBanner />
  </div>

  <div v-if="!isAdmin" class="gap-4 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3">
    <IncomingCollectsCard />
    <ComplianceScoreCard />
    <AccountManager class="!w-full" />
  </div>

  <Dashboard v-if="featureFlags.wpMetabaseDashboard.value" />

  <div class="flex flex-col gap-7">
    <div class="flex flex-col gap-2">
      <Title :title="t('global.start_infos')" />
      <LightSubtitle :subtitle="t('global.start_infos_desc')" />
    </div>

    <iframe
      class="drop-shadow-lg rounded-xl"
      src="https://common-seal-588.notion.site/ebd/1d21fa984251805faf32e682e80a0d5f"
      width="100%"
      height="600px"
      frameborder="0"
      allowfullscreen
    />
  </div>
</template>
